import { useId } from "react";
import type { ReactNode } from "react";
import styles from "./Field.module.css";
import { VisuallyHidden } from "../VisuallyHidden";

/**
 * One labelled control.
 *
 * Every field on Dealbid is built the same way, because the accessible wiring is the part
 * that is easy to get subtly wrong and impossible to see: a real `<label>` bound by `id`,
 * the hint and the error both named in `aria-describedby` so a screen reader reads the
 * guidance *and* the problem, and `aria-invalid` so the state is exposed rather than
 * implied by a red border.
 *
 * The id is generated per instance rather than derived from the field name. The report form
 * is rendered once per deal card, so a name-derived id would repeat down the page and quietly
 * bind several labels to the same input.
 *
 * An error is never carried by colour alone (canon `contracts/DESIGN-TOKENS.md`): it has a
 * marker glyph, the word "Error" for assistive technology, and the message itself.
 */

interface FieldShellProps {
  name: string;
  label: string;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  /**
   * Hide the label visually, keeping it a real `<label>` bound by `id`.
   *
   * For a field whose surrounding card already names it in a heading — the footer's
   * subscribe card, which the design canvas draws as a bare field beside its button
   * (`WEB-013`). A placeholder is **not** a label: it disappears on the first keystroke and
   * is not announced as a name, so the label still exists here and is still bound.
   */
  labelHidden?: boolean | undefined;
  /** Rendered under the label; receives the wiring the control must apply. */
  children: (control: {
    id: string;
    name: string;
    "aria-describedby": string | undefined;
    "aria-invalid": boolean | undefined;
    required: boolean | undefined;
  }) => ReactNode;
}

function FieldShell({
  name,
  label,
  hint,
  error,
  required,
  labelHidden = false,
  children,
}: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint === undefined ? null : hintId, error === undefined ? null : errorId]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={styles.field}>
      <label
        className={labelHidden ? styles.labelHidden : styles.label}
        htmlFor={id}
      >
        {label}
        {required === true ? null : <span className={styles.optional}> (optional)</span>}
      </label>
      {hint === undefined ? null : (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      )}
      {children({
        id,
        name,
        "aria-describedby": describedBy,
        "aria-invalid": error === undefined ? undefined : true,
        required,
      })}
      {error === undefined ? null : (
        <p className={styles.error} id={errorId}>
          <svg className={styles.marker} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M8 4.6v4.2M8 11v.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <VisuallyHidden>Error: </VisuallyHidden>
          {error}
        </p>
      )}
    </div>
  );
}

interface ValueProps {
  value: string;
  onChange: (value: string) => void;
}

type CommonProps = Omit<FieldShellProps, "children"> & ValueProps;

export function TextField({
  value,
  onChange,
  type = "text",
  inputMode,
  autoComplete,
  maxLength,
  placeholder,
  ...shell
}: CommonProps & {
  type?: "text" | "email" | "url" | undefined;
  inputMode?: "text" | "email" | "url" | "decimal" | undefined;
  autoComplete?: string | undefined;
  maxLength?: number | undefined;
  placeholder?: string | undefined;
}) {
  return (
    <FieldShell {...shell}>
      {(control) => (
        <input
          {...control}
          className={styles.control}
          type={type}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          {...(inputMode === undefined ? {} : { inputMode })}
          {...(autoComplete === undefined ? {} : { autoComplete })}
          {...(maxLength === undefined ? {} : { maxLength })}
          {...(placeholder === undefined ? {} : { placeholder })}
        />
      )}
    </FieldShell>
  );
}

export function TextAreaField({
  value,
  onChange,
  rows = 3,
  maxLength,
  ...shell
}: CommonProps & { rows?: number | undefined; maxLength?: number | undefined }) {
  return (
    <FieldShell {...shell}>
      {(control) => (
        <textarea
          {...control}
          className={`${styles.control} ${styles.textarea}`}
          rows={rows}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          {...(maxLength === undefined ? {} : { maxLength })}
        />
      )}
    </FieldShell>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export function SelectField({
  value,
  onChange,
  options,
  ...shell
}: CommonProps & { options: readonly SelectOption[] }) {
  return (
    <FieldShell {...shell}>
      {(control) => (
        <select
          {...control}
          className={`${styles.control} ${styles.select}`}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  );
}

/**
 * A checkbox states its own condition in its label, so the label carries the sentence
 * rather than a noun. Consent and terms are the two uses, and both have to be readable as
 * "what am I agreeing to" without looking anywhere else on the page.
 */
export function CheckboxField({
  name,
  label,
  hint,
  error,
  checked,
  onChange,
}: {
  name: string;
  label: ReactNode;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint === undefined ? null : hintId, error === undefined ? null : errorId]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={styles.checkboxField}>
      <div className={styles.checkboxRow}>
        <input
          id={id}
          name={name}
          type="checkbox"
          className={styles.checkbox}
          checked={checked}
          aria-describedby={describedBy}
          aria-invalid={error === undefined ? undefined : true}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
        <label className={styles.checkboxLabel} htmlFor={id}>
          {label}
        </label>
      </div>
      {hint === undefined ? null : (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      )}
      {error === undefined ? null : (
        <p className={styles.error} id={errorId}>
          <svg className={styles.marker} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M8 4.6v4.2M8 11v.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <VisuallyHidden>Error: </VisuallyHidden>
          {error}
        </p>
      )}
    </div>
  );
}

/*
 * `FieldGroup` and `FieldRow` left with the submission form (`WEB-014`). They were a
 * `<fieldset>`/`<legend>` grouping and a two-column price pair, and nothing this site still
 * shows has more than a handful of fields in a row. `dealbid-app` owns the long form now.
 */
